import { DIGISAKA_API_ORIGIN } from '../config/digisakaApiOrigin';
import {
  digisakaAuthRequestInit,
  digisakaMeteredAuthHeaders,
  gptInferenceJsonHeaders,
} from '../config/digisakaGptInference';

/** SessionStorage key for restoring the active thread after refresh. */
export const DIGISAKA_GPT_CONVERSATION_STORAGE_KEY = 'digisaka_gpt_active_conversation_id';

export type GptConversationSummary = {
  id: string;
  title: string;
  pinned?: boolean;
  is_favourite?: boolean;
  updated_at?: string | null;
};

export type GptMemoryItem = {
  id: string;
  content: string;
  created_at?: string | null;
};

export type StoredGptMessageRow = {
  id: string;
  role: string;
  content: unknown;
  created_at?: string | null;
};

export async function createDigisakaGptConversation(): Promise<{ id: string }> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/digisakgpt/conversations`,
    digisakaAuthRequestInit({
      method: 'POST',
      headers: { ...gptInferenceJsonHeaders() },
    })
  );
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  return res.json();
}

export async function listDigisakaGptConversations(): Promise<GptConversationSummary[]> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/digisakgpt/conversations`,
    digisakaAuthRequestInit({
      headers: { ...digisakaMeteredAuthHeaders() },
    })
  );
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  const data = await res.json();
  return (data.items ?? []).map(
    (i: {
      id: string;
      title?: string;
      pinned?: boolean;
      is_favourite?: boolean;
      updated_at?: string | null;
    }) => ({
      id: i.id,
      title: i.title || 'New chat',
      pinned: Boolean(i.pinned),
      is_favourite: Boolean(i.is_favourite),
      updated_at: i.updated_at ?? null,
    })
  );
}

export async function fetchDigisakaGptMessages(conversationId: string): Promise<StoredGptMessageRow[]> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/digisakgpt/conversations/${encodeURIComponent(conversationId)}/messages`,
    digisakaAuthRequestInit({
      headers: { ...digisakaMeteredAuthHeaders() },
    })
  );
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  const data = await res.json();
  return data.messages ?? [];
}

export async function renameDigisakaGptConversation(
  conversationId: string,
  title: string
): Promise<GptConversationSummary> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/digisakgpt/conversations/${encodeURIComponent(conversationId)}`,
    digisakaAuthRequestInit({
      method: 'PATCH',
      headers: { ...gptInferenceJsonHeaders() },
      body: JSON.stringify({ title: title.trim() }),
    })
  );
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  const data = await res.json();
  return {
    id: data.id,
    title: data.title || 'New chat',
    pinned: Boolean(data.pinned),
    is_favourite: Boolean(data.is_favourite),
    updated_at: data.updated_at ?? null,
  };
}

export async function updateDigisakaGptConversationFlags(
  conversationId: string,
  flags: { pinned?: boolean; is_favourite?: boolean }
): Promise<GptConversationSummary> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/digisakgpt/conversations/${encodeURIComponent(conversationId)}`,
    digisakaAuthRequestInit({
      method: 'PATCH',
      headers: { ...gptInferenceJsonHeaders() },
      body: JSON.stringify(flags),
    })
  );
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  const data = await res.json();
  return {
    id: data.id,
    title: data.title || 'New chat',
    pinned: Boolean(data.pinned),
    is_favourite: Boolean(data.is_favourite),
    updated_at: data.updated_at ?? null,
  };
}

export async function listDigisakaGptMemories(): Promise<GptMemoryItem[]> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/digisakgpt/memories`,
    digisakaAuthRequestInit({
      headers: { ...digisakaMeteredAuthHeaders() },
    })
  );
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
  const data = await res.json();
  return (data.items ?? []).map((i: GptMemoryItem) => ({
    id: i.id,
    content: i.content,
    created_at: i.created_at ?? null,
  }));
}

export async function deleteDigisakaGptMemory(memoryId: string): Promise<void> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/digisakgpt/memories/${encodeURIComponent(memoryId)}`,
    digisakaAuthRequestInit({
      method: 'DELETE',
      headers: { ...digisakaMeteredAuthHeaders() },
    })
  );
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
}

export async function deleteDigisakaGptConversation(conversationId: string): Promise<void> {
  const res = await fetch(
    `${DIGISAKA_API_ORIGIN}/api/digisakgpt/conversations/${encodeURIComponent(conversationId)}`,
    digisakaAuthRequestInit({
      method: 'DELETE',
      headers: { ...digisakaMeteredAuthHeaders() },
    })
  );
  if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
}
